/**
 * "Add User" modal for Pool operator access (corrected flow, 21/7) — replaces the old ALWAYS-VISIBLE
 * inline "grant access" section on Pool Settings (search picker + per-row role Select) with a single
 * "Add User" button opening this modal. Flow order, deliberately select-THEN-role (not the old
 * side-by-side layout, which let a role be picked before a user was even chosen):
 *
 *  ① search box (usePoolUserCandidates, live) — rows show email + portal role; a candidate who is
 *     already an Owner or already holds SOME pool row is DISABLED with the reason shown as a badge
 *     (client-side mirror of the server's own 422 for the Owner case — same "กันงง" reasoning as the old
 *     inline picker). Clicking an enabled row selects it (highlighted).
 *  ② once a user is selected, the role chooser appears: Admin/Viewer as radio cards with a one-line
 *     description each. Changing the selected user does NOT reset the chosen role — only choosing a
 *     DIFFERENT user changes anything about the user; the role stays whatever it was.
 *  ③ "Add" is enabled only once BOTH a user and a role are chosen → POST /operators. Success closes the
 *     modal, refreshes the operators list, and fires the success toast (same copy as the old picker's
 *     grantSuccess). A 409/422 renders INLINE (role="alert") instead of a toast — the exact same reason
 *     as AddAccountModal's own deviation #3: a toast would draw behind this native <dialog>'s own
 *     top-layer backdrop while it's still open.
 *
 * The Owner-pinned operators list itself (PoolSettingsPage) is untouched by this change.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { ApiRequestError } from "@/api/client";
import {
  Badge,
  Button,
  EmptyState,
  Input,
  Modal,
  SkeletonRows,
  useNotifier,
} from "@/components/ui";
import { PoolErrorState } from "@/features/pool/PoolErrorState";
import {
  MIN_CANDIDATE_CHARS,
  useAddPoolOperator,
  usePoolUserCandidates,
} from "@/features/pool/hooks";
import { RoleCard } from "@/features/pool/RoleCards";
import { cn } from "@/lib/cn";
import type { PoolOperatorRole, PoolUserCandidate, Role } from "@/api/types";
import type { TKey } from "@/i18n/keys";

function errMessage(e: unknown, fallback: string): string {
  return e instanceof ApiRequestError ? e.error.message : fallback;
}

/** Duplicated locally rather than imported from PoolSettingsPage (not exported there either) — same
 *  convention as AddAccountModal's own local errMessage()/accountLabel(): a tiny local const per modal
 *  file beats a shared import for a 4-entry map used in exactly one place each. */
const PORTAL_ROLE_LABEL_KEY: Record<Role, TKey> = {
  owner: "role.owner",
  admin: "role.admin",
  viewer: "role.viewer",
  custom: "role.custom",
};

export function AddUserModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { notify } = useNotifier();
  const [term, setTerm] = useState("");
  const candidatesQ = usePoolUserCandidates(term);
  const addMut = useAddPoolOperator();

  const [selected, setSelected] = useState<PoolUserCandidate | null>(null);
  const [role, setRole] = useState<PoolOperatorRole | null>(null);
  const [error, setError] = useState<string | null>(null);

  function submit() {
    if (!selected || !role) return;
    setError(null);
    addMut.mutate(
      { email: selected.email, role },
      {
        onSuccess: () => {
          notify({ tone: "ok", title: t("pool.settings.grantSuccess", { email: selected.email }) });
          onClose();
        },
        onError: (e) => setError(errMessage(e, t("state.errorTitle"))),
      },
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t("pool.settings.addUserModalTitle")}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("action.cancel")}
          </Button>
          <Button loading={addMut.isPending} disabled={!selected || !role} onClick={submit}>
            {t("pool.settings.addUserSubmitButton")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="relative w-full">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-(--text-subtle)"
            aria-hidden
          />
          <Input
            type="search"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder={t("pool.settings.userSearchPlaceholder")}
            aria-label={t("pool.settings.userSearchLabel")}
            autoComplete="off"
            spellCheck={false}
            className="h-9 w-full pl-9"
          />
        </div>

        {term.trim().length < MIN_CANDIDATE_CHARS ? (
          <p className="text-(length:--text-xs) text-(--text-subtle)">
            {t("pool.settings.userSearchHint")}
          </p>
        ) : candidatesQ.isPending ? (
          <SkeletonRows rows={3} />
        ) : candidatesQ.isError ? (
          <PoolErrorState error={candidatesQ.error} onRetry={() => void candidatesQ.refetch()} />
        ) : (
          <CandidateList
            candidates={candidatesQ.data.candidates}
            selectedEmail={selected?.email ?? null}
            onSelect={setSelected}
          />
        )}

        {selected && (
          <div className="flex flex-col gap-2 border-t border-(--border-hairline) pt-4">
            <h3 className="text-(length:--text-sm) font-semibold text-(--text-strong)">
              {t("pool.settings.addUserRoleStepTitle")}
            </h3>
            <div
              role="radiogroup"
              aria-label={t("pool.settings.addUserRoleStepTitle")}
              className="grid grid-cols-1 gap-3 sm:grid-cols-2"
            >
              <RoleCard
                title={t("role.admin")}
                description={t("pool.settings.addUserRoleAdminDesc")}
                selected={role === "admin"}
                onSelect={() => setRole("admin")}
              />
              <RoleCard
                title={t("role.viewer")}
                description={t("pool.settings.addUserRoleViewerDesc")}
                selected={role === "viewer"}
                onSelect={() => setRole("viewer")}
              />
            </div>
          </div>
        )}

        {/* Rendered inline rather than as a toast — same reasoning as AddAccountModal's deviation #3: a
            toast would draw behind this modal's own top-layer backdrop while the dialog is still open. */}
        {error && (
          <p role="alert" className="text-(length:--text-sm) text-(--danger)">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

function CandidateList({
  candidates,
  selectedEmail,
  onSelect,
}: {
  candidates: PoolUserCandidate[];
  selectedEmail: string | null;
  onSelect: (c: PoolUserCandidate) => void;
}) {
  const { t } = useTranslation();

  if (candidates.length === 0) {
    return (
      <EmptyState
        title={t("pool.settings.candidatesEmptyTitle")}
        body={t("pool.settings.candidatesEmptyBody")}
      />
    );
  }

  return (
    <ul
      role="listbox"
      aria-label={t("pool.settings.userSearchLabel")}
      className="flex max-h-64 flex-col gap-1 overflow-auto rounded-(--radius) border border-(--border) bg-(--surface) p-1 shadow-(--shadow-sm)"
    >
      {candidates.map((c) => {
        // Already has full access (Owner) or already holds a pool row — nothing left to grant; same
        // client-side mirror of the server's 422 the old inline picker had (rev 6 R1).
        const disabledReason =
          c.portal_role === "owner"
            ? t("pool.settings.candidateOwnerNote")
            : c.is_pool_operator
              ? t("pool.settings.candidateAlreadyOperator")
              : null;
        const isSelected = selectedEmail === c.email;
        return (
          <li key={c.email}>
            <button
              type="button"
              role="option"
              aria-selected={isSelected}
              disabled={Boolean(disabledReason)}
              onClick={() => onSelect(c)}
              className={cn(
                "flex w-full items-center justify-between gap-3 rounded-(--radius-sm) px-3 py-2 text-left text-(length:--text-sm) transition-colors",
                "focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-(--brand)",
                "disabled:cursor-not-allowed disabled:opacity-60",
                isSelected
                  ? "bg-(--brand-subtle) text-(--brand-strong)"
                  : "text-(--text) hover:bg-(--surface-hover)",
              )}
            >
              <span className="flex flex-col">
                <span className="font-medium">{c.email}</span>
                <span className="text-(length:--text-2xs) text-(--text-muted)">
                  {c.portal_role ? t(PORTAL_ROLE_LABEL_KEY[c.portal_role]) : "—"}
                </span>
              </span>
              {disabledReason && <Badge tone="neutral">{disabledReason}</Badge>}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
